import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  DollarSign, 
  TrendingUp, 
  Hash, 
  Calendar,
  RefreshCw,
  ArrowUpRight,
  CreditCard,
  Wallet,
  CheckCircle,
  Clock,
  XCircle,
  Gift,
  Shield,
  User
} from 'lucide-react';

const FundHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    currentBalance: 0,
    totalAdded: 0,
    totalPending: 0,
    totalGross: 0,
    netAmount: 0,
    count: 0,
    pendingCount: 0,
    adminAddedCount: 0,
    bonusCount: 0,
    userAddedCount: 0,
    creditedCount: 0,
    debitedCount: 0,
    totalCredited: 0,
    totalDebited: 0,
    userUsedAmount: 0,
    userUsedCount: 0,
    balanceAfterUse: 0,
    lastUpdated: ''
  });

  const userId = localStorage.getItem('userId');
  const userDbKey = localStorage.getItem('dbKey');
  const userRegistrationDate = localStorage.getItem('registrationDate');
  const userSponsorId = localStorage.getItem('userSponsorId') || userId;

  useEffect(() => {
    if (userDbKey || userId) {
      fetchFundHistory();
    }
  }, [userDbKey, userId]);

  // Helper function to determine if transaction is a Welcome Bonus
  const isWelcomeBonus = (transaction) => {
    // Check by payment method
    if (transaction.paymentMethod === 'bonus' || 
        transaction.paymentDisplayMethod === 'Registration Welcome Bonus') {
      return true;
    }
    
    // Check by description (only exact welcome bonus match)
    if (transaction.description) {
      const desc = transaction.description.toLowerCase();
      if (desc.includes('welcome bonus') && 
          !desc.includes('user added') && 
          !desc.includes('admin')) {
        return true;
      }
    }
    
    // Check by amount and source combination
    if (transaction.amount === 3 && 
        transaction.source === 'Aerovolt' && 
        !transaction.paymentId?.startsWith('FUND_')) {
      return true;
    }
    
    return false;
  };

  // Helper function to determine if transaction is User Added (via payment)
  const isUserAdded = (transaction) => {
    if (transaction.paymentId && 
        !transaction.paymentId.startsWith('BONUS_') &&
        !transaction.paymentId.startsWith('FUND_')) {
      return true;
    }
    
    if (transaction.currency && 
        transaction.paymentMethod !== 'bonus' &&
        !transaction.description?.toLowerCase().includes('welcome bonus')) {
      return true;
    }
    
    return false;
  };

  // Helper function to determine if transaction is Admin Added
  const isAdminAdded = (transaction) => {
    return transaction.source === 'admin' || 
           transaction.addedBy === 'admin' ||
           transaction.description?.toLowerCase().includes('admin') ||
           transaction.walletType === 'Admin Addition';
  };

  const isAgWalletOnly = (walletType = '', transactionType = '') => {
    const wallet = String(walletType || '').toLowerCase();
    const type = String(transactionType || '').toLowerCase();
    if (wallet.includes('av power') || type.includes('av power')) return false;
    if (wallet.includes('power')) return false;
    if (!wallet) return true;
    return wallet.includes('av wallet') || wallet.includes('av pay') || wallet.includes('fund');
  };

  const getHistoryAmount = (tx) => {
    const status = String(tx?.status || '').toLowerCase();
    const baseAmount = Number(tx?.amount);
    const outcomeAmount = Number(tx?.outcome_amount ?? tx?.outcomeAmount);
    const paidFiatAmount = Number(tx?.actually_paid_at_fiat ?? tx?.actuallyPaidAtFiat);

    if ((status === 'completed' || status === 'success' || status === 'finished') && outcomeAmount > 0) {
      return outcomeAmount;
    }
    if (baseAmount > 0) return baseAmount;
    if (paidFiatAmount > 0) return paidFiatAmount;
    if (outcomeAmount > 0) return outcomeAmount;
    return 0;
  };

  const fetchFundHistory = async () => {
    try {
      setLoading(true);
      
      // First get current balance and used amount from user data
      let currentBalance = 0;
      let userUsedAmount = 0;
      let userUsedCount = 0;
      
      if (userRegistrationDate && userDbKey) {
        const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
        const userSnapshot = await get(userRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          currentBalance =
            Number(userData?.wallets?.agWallet) ||
            Number(userData?.wallets?.agPayWallet) ||
            Number(userData?.fund?.balance) ||
            0;

          // User used amount from package activations
          if (Array.isArray(userData?.packages)) {
            userData.packages.forEach((pkg) => {
              if (!pkg || typeof pkg !== 'object') return;
              const walletUsed = String(pkg.walletUsed || '').toLowerCase();
              const usedFromAgWallet =
                walletUsed.includes('av wallet') ||
                walletUsed.includes('av pay') ||
                walletUsed.includes('aerovolt');
              if (!usedFromAgWallet) return;
              const used = Number(pkg.totalAmount || pkg.investmentAmount || 0);
              if (used > 0) {
                userUsedAmount += used;
                userUsedCount += 1;
              }
            });
          }
        }
      }
      
      let transactionsList = [];
      let totalAdded = 0;
      let totalPending = 0;
      let pendingCount = 0;
      let adminAddedCount = 0;
      let bonusCount = 0;
      let userAddedCount = 0;
      
      // Helper function to process a transaction
      const processTransaction = (tx, txId, date, source) => {
        // Determine transaction type
        const bonus = isWelcomeBonus(tx);
        const userAdded = !bonus && isUserAdded(tx);
        const adminAdded = isAdminAdded(tx);
        
        // Determine status
        let isCompleted = false;
        if (bonus) {
          isCompleted = true;
          bonusCount++;
        } else if (tx.status) {
          isCompleted = tx.status === 'completed' || tx.status === 'success';
        } else {
          isCompleted = tx.newBalance !== undefined || tx.fundBalance !== undefined;
        }
        
        // Get proper description
        let description = tx.description || 'Fund Addition';
        if (bonus && !description.toLowerCase().includes('welcome')) {
          description = 'Welcome Bonus for Registration';
        } else if (userAdded) {
          description = `User added funds via ${tx.displayCurrency || tx.currency || 'Crypto'}`;
          userAddedCount++;
        }
        
        const transaction = {
          id: txId,
          date: date,
          amount: getHistoryAmount(tx),
          previousBalance: parseFloat(tx.previousFundBalance || tx.previousBalance) || 0,
          newBalance: parseFloat(tx.fundBalance || tx.newBalance) || 0,
          paymentMethod: tx.displayCurrency || tx.currency || tx.paymentDisplayMethod || 'Crypto',
          status: tx.status || (bonus ? 'completed' : 'pending'),
          timestamp: tx.timestampFormatted || tx.timestamp || date,
          description: description,
          source: tx.source || source || 'User',
          network: tx.network || '',
          currency: tx.currency || '',
          transactionId: tx.transactionId || tx.paymentId || txId,
          paymentId: tx.paymentId,
          isCompleted: isCompleted,
          type: bonus ? 'bonus' : (userAdded ? 'user_added' : (adminAdded ? 'admin' : 'other')),
          addedBy: bonus ? 'Aerovolt' : (adminAdded ? 'Admin' : null),
          walletType: tx.walletType || 'AV Wallet',
          payinAddress: tx.payinAddress || tx.pay_address || tx.payAddress || ''
        };

        if (!isAgWalletOnly(transaction.walletType, tx.transactionType)) return null;
        
        if (isCompleted) {
          totalAdded += transaction.amount;
        } else {
          totalPending += transaction.amount;
          pendingCount++;
        }
        
        return transaction;
      };

      // ============ FETCH FROM UserFundTransactions ============
      try {
        const txRef = ref(database, 'UserFundTransactions');
        const snapshot = await get(txRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          
          // Search through all dates
          Object.entries(data).forEach(([date, dateGroup]) => {
            Object.entries(dateGroup).forEach(([userKey, userGroup]) => {
              // Check if this group belongs to current user
              const isCurrentUser = 
                userKey === userDbKey || 
                userKey === userId || 
                userKey === userSponsorId;
              
              if (isCurrentUser) {
                Object.entries(userGroup).forEach(([txId, tx]) => {
                  const transaction = processTransaction(tx, txId, date, 'User');
                  if (transaction) transactionsList.push(transaction);
                });
              } else {
                // Check individual transactions
                Object.entries(userGroup).forEach(([txId, tx]) => {
                  const belongsToUser = 
                    tx.userId === userId || 
                    tx.memberId === userDbKey || 
                    tx.userId === userSponsorId ||
                    tx.sponsorId === userSponsorId;
                  
                  if (belongsToUser) {
                    const transaction = processTransaction(tx, txId, date, 'User');
                    if (transaction) transactionsList.push(transaction);
                  }
                });
              }
            });
          });
        }
      } catch (error) {
        console.error('Error fetching UserFundTransactions:', error);
      }
      
      // ============ FETCH FROM adminaddedhistory ============
      try {
        const adminHistoryRef = ref(database, 'adminaddedhistory');
        const adminHistorySnapshot = await get(adminHistoryRef);
        
        if (adminHistorySnapshot.exists()) {
          const data = adminHistorySnapshot.val();
          
          Object.entries(data).forEach(([dateKey, dateData]) => {
            Object.entries(dateData).forEach(([sponsorId, sponsorTransactions]) => {
              const isCurrentUser = 
                sponsorId === userSponsorId || 
                sponsorId === userId || 
                sponsorId === userDbKey;
              
              if (isCurrentUser) {
                if (Array.isArray(sponsorTransactions)) {
                  sponsorTransactions.forEach((tx, index) => {
                    if (tx) {
                      if (!isAgWalletOnly(tx.walletType, tx.transactionType)) return;

                      const transaction = {
                        id: tx.transactionId || `admin_${dateKey}_${sponsorId}_${index}`,
                        date: dateKey,
                        amount: parseFloat(tx.amount) || 0,
                        previousBalance: parseFloat(tx.previousBalance) || 0,
                        newBalance: parseFloat(tx.newBalance) || 0,
                        paymentMethod: tx.walletType || 'Admin Addition',
                        status: 'completed',
                        timestamp: tx.date || dateKey,
                        description: tx.description || tx.transactionType || 'Admin Fund Addition',
                        addedBy: 'Admin',
                        walletType: tx.walletType || 'AV Wallet',
                        transactionType: tx.transactionType,
                        source: 'Aerovolt',
                        isCompleted: true,
                        type: 'admin',
                        network: '',
                        currency: ''
                      };
                      
                      transactionsList.push(transaction);
                      totalAdded += transaction.amount;
                      adminAddedCount++;
                    }
                  });
                }
              }
            });
          });
        }
      } catch (error) {
        console.error('Error fetching adminaddedhistory:', error);
      }
      
      // De-duplicate transactions
      const completedFundPaymentIds = new Set(
        transactionsList
          .filter((tx) => String(tx?.id || '').startsWith('FUND_') && tx?.paymentId)
          .map((tx) => String(tx.paymentId))
      );

      const seenCanonicalKeys = new Set();
      transactionsList = transactionsList.filter((tx) => {
        const paymentId = tx?.paymentId ? String(tx.paymentId) : '';
        if (paymentId && !String(tx?.id || '').startsWith('FUND_') && completedFundPaymentIds.has(paymentId)) {
          return false;
        }

        const canonicalTxKey = String(
          tx?.transactionId ||
          tx?.id ||
          tx?.paymentId ||
          ''
        ).trim();

        if (!canonicalTxKey) return true;
        if (seenCanonicalKeys.has(canonicalTxKey)) return false;
        seenCanonicalKeys.add(canonicalTxKey);
        return true;
      });

      // Sort by timestamp (newest first)
      transactionsList.sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime() || 0;
        const timeB = new Date(b.timestamp).getTime() || 0;
        return timeB - timeA;
      });

      const totalGross = transactionsList.reduce((sum, tx) => sum + Math.abs(Number(tx.amount || 0)), 0);
      const netAmount = transactionsList.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
      const creditedTransactions = transactionsList.filter((tx) => Number(tx.amount || 0) >= 0);
      const debitedTransactions = transactionsList.filter((tx) => Number(tx.amount || 0) < 0);
      const totalCredited = creditedTransactions.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
      const totalDebited = debitedTransactions.reduce((sum, tx) => sum + Math.abs(Number(tx.amount || 0)), 0);
      
      const dedupTotalAdded = transactionsList
        .filter((tx) => tx.isCompleted)
        .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
      const dedupPendingTransactions = transactionsList.filter((tx) => !tx.isCompleted);
      const dedupTotalPending = dedupPendingTransactions.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
      const dedupPendingCount = dedupPendingTransactions.length;
      const dedupBonusCount = transactionsList.filter((tx) => tx.type === 'bonus').length;
      const dedupUserAddedCount = transactionsList.filter((tx) => tx.type === 'user_added').length;
      const dedupAdminAddedCount = transactionsList.filter((tx) => tx.type === 'admin').length;
      const balanceAfterUse = currentBalance - userUsedAmount;
      
      setTransactions(transactionsList);
      setStats({
        currentBalance,
        totalAdded: dedupTotalAdded,
        totalPending: dedupTotalPending,
        totalGross,
        netAmount,
        count: transactionsList.length,
        pendingCount: dedupPendingCount,
        adminAddedCount: dedupAdminAddedCount,
        bonusCount: dedupBonusCount,
        userAddedCount: dedupUserAddedCount,
        creditedCount: creditedTransactions.length,
        debitedCount: debitedTransactions.length,
        totalCredited,
        totalDebited,
        userUsedAmount,
        userUsedCount,
        balanceAfterUse,
        lastUpdated: new Date().toLocaleTimeString()
      });
    } catch (error) {
      console.error('Error fetching fund history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (transaction) => {
    if (transaction.type === 'bonus') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </span>
      );
    }
    
    if (transaction.status === 'completed' || transaction.status === 'success' || transaction.isCompleted) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </span>
      );
    }
    if (transaction.status === 'pending') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>
      );
    }
    if (transaction.status === 'failed' || transaction.status === 'expired') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400 border border-gray-500/30">
        {transaction.status}
      </span>
    );
  };

  const getTransactionIcon = (transaction) => {
    switch(transaction.type) {
      case 'bonus':
        return <Gift className="h-5 w-5 text-purple-400" />;
      case 'admin':
        return <Shield className="h-5 w-5 text-blue-400" />;
      case 'user_added':
        return <User className="h-5 w-5 text-green-400" />;
      default:
        return <ArrowUpRight className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTypeBadge = (transaction) => {
    switch(transaction.type) {
      case 'bonus':
        return (
          <span className="text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded">
            Welcome Bonus
          </span>
        );
      case 'admin':
        return (
          <span className="text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-1 rounded">
            Admin Added
          </span>
        );
      case 'user_added':
        return (
          <span className="text-xs bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-1 rounded">
            User Added
          </span>
        );
      default:
        return null;
    }
  };

  const getCreditDebitBadge = (transaction) => {
    const amount = Number(transaction?.amount || 0);
    const status = amount < 0 ? 'Debited' : 'Credited';
    return (
      <span className={`text-xs border px-2 py-1 rounded ${
        status === 'Debited'
          ? 'bg-red-500/20 text-red-400 border-red-500/30'
          : 'bg-green-500/20 text-green-400 border-green-500/30'
      }`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">AV PAY Wallet History</h1>
      <p className="text-gray-400 mb-6">Your fund addition and transaction history</p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Current Balance</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.currentBalance)}</div>
          <div className="text-gray-500 text-[10px]">
            After Use: {formatCurrency(stats.balanceAfterUse)}
          </div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Amount (Gross)</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.totalGross)}</div>
          <div className="text-gray-500 text-[10px]">
            C: {formatCurrency(stats.totalCredited)} | D: {formatCurrency(stats.totalDebited)}
          </div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Pending</div>
          <div className="text-lg font-bold text-yellow-400">{formatCurrency(stats.totalPending)}</div>
          <div className="text-gray-500 text-[10px]">{stats.pendingCount} transactions</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Transactions</div>
          <div className="text-lg font-bold text-blue-400">{stats.count}</div>
          <div className="text-gray-500 text-[10px]">C: {stats.creditedCount} | D: {stats.debitedCount}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Net Movement</div>
          <div className={`text-lg font-bold ${stats.netAmount < 0 ? 'text-red-400' : 'text-green-400'}`}>
            {formatCurrency(stats.netAmount)}
          </div>
          <div className="text-xs text-gray-300">
            <div>Bonus: {stats.bonusCount}</div>
            <div>User: {stats.userAddedCount}</div>
            <div>Admin: {stats.adminAddedCount}</div>
            <div>Used: {formatCurrency(stats.userUsedAmount)} ({stats.userUsedCount})</div>
          </div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Last Updated</div>
          <div className="text-lg font-bold text-gray-400 text-sm">{stats.lastUpdated}</div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="mb-6">
        <button
          onClick={fetchFundHistory}
          className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Transactions List */}
      <div className="space-y-4">
        {transactions.length > 0 ? (
          transactions.map((transaction) => {
            return (
              <div key={transaction.id} className={`card-glow p-4 bg-gray-800/50 rounded-xl ${
                !transaction.isCompleted ? 'border-l-4 border-yellow-500/50' : ''
              }`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Left Section - Transaction Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        transaction.type === 'bonus' ? 'bg-purple-500/20' :
                        transaction.type === 'admin' ? 'bg-blue-500/20' :
                        transaction.type === 'user_added' ? 'bg-green-500/20' :
                        transaction.isCompleted ? 'bg-green-500/20' : 'bg-yellow-500/20'
                      }`}>
                        {getTransactionIcon(transaction)}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="text-white font-bold text-lg">{formatCurrency(transaction.amount)}</h3>
                          {getStatusBadge(transaction)}
                          {getCreditDebitBadge(transaction)}
                          {getTypeBadge(transaction)}
                        </div>
                        <p className="text-gray-300 text-sm mb-1">
                          {transaction.description}
                          {!transaction.isCompleted && transaction.status === 'pending' && (
                            <span className="text-yellow-400 text-xs ml-2">(Awaiting confirmation)</span>
                          )}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 text-gray-400 text-xs">
                          <span className="flex items-center gap-1">
                            <Wallet className="h-3 w-3" />
                            {transaction.walletType || 'AV Wallet'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(transaction.timestamp)}
                          </span>
                          {transaction.addedBy && (
                            <span className={`${
                              transaction.type === 'bonus' ? 'text-purple-400' :
                              transaction.type === 'admin' ? 'text-blue-400' :
                              'text-green-400'
                            }`}>
                              By: {transaction.addedBy}
                            </span>
                          )}
                          {transaction.network && (
                            <span className="text-gray-500">{transaction.network}</span>
                          )}
                          {transaction.transactionId && (
                            <span className="text-gray-500 font-mono text-[10px]">
                              ID: {transaction.transactionId.substring(0, 8)}...
                            </span>
                          )}
                          {transaction.payinAddress && (
                            <span className="text-gray-500 font-mono text-[10px]">
                              Payin: {transaction.payinAddress}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Section - Balance Changes */}
                  <div className="md:text-right">
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                      <div>
                        <div className="text-gray-400 text-xs">Before</div>
                        <div className="text-gray-300 text-sm">{formatCurrency(transaction.previousBalance)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs">After</div>
                        <div className={`font-bold ${
                          transaction.isCompleted ? 'text-green-400' : 'text-gray-400'
                        }`}>
                          {formatCurrency(transaction.newBalance)}
                          {!transaction.isCompleted && (
                            <span className="text-yellow-400 text-xs block">Pending</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Fund Transactions</h3>
            <p className="text-gray-400 mb-6">You haven't added any funds yet</p>
            <button
              onClick={() => window.location.href = '/user-dashboard/addfund'}
              className="px-6 py-3 bg-primary hover:bg-primary/80 rounded-lg text-white font-bold"
            >
              Add Funds Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundHistory;